import CloseIcon from '@mui/icons-material/Close';
import {
   Checkbox,
   DialogActions,
   DialogContent,
   DialogTitle,
   FormControl,
   FormControlLabel,
   FormGroup,
   Radio,
   RadioGroup,
} from '@mui/material';
import { addDays, format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SaveIcon14pxSvg from '../../../assets/images/icons/SaveIcon14pxSvg';
import { editRosterItem } from '../../../axios/RosterItem/RosterItemApi';
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog } from '../../../components/StyledComponents';
import { useLoading } from '../../../context/LoadingContext';
import { useSettings } from '../../../context/SettingsContext';
import CustomSelect from '../../../components/CustomSelect';

const EditRosterItemsDialog = ({ shiftsData, onClose, selectedRosterItem, onRefresh, genaralSettingDateFormat, selectedRows }) => {
   const { t } = useTranslation();
   const { settings } = useSettings();
   const [show, hide] = useLoading();
   // const [endOfRepitition, setEndOfRepitition] = useState(true);
   const [endOfRepitition, setEndOfRepitition] = useState(!!selectedRosterItem?.endDate);
   const filteredShifts =  shiftsData.filter(shift => shift.shiftName !== "DEFAULT");

   const rosterStartDate = selectedRows?.startDate
      ? new Date(selectedRows.startDate)
      : null;
   const rosterEndDate = selectedRows?.endBy
      ? new Date(selectedRows.endBy)
      : null;

   const initialFormData = {
      startDate: new Date(selectedRosterItem.startDate),
      endDate: selectedRosterItem?.endDate ? new Date(selectedRosterItem.endDate) : new Date(),
      shift: selectedRosterItem.shiftId,
      rosterItemId: selectedRosterItem.rosterItemId

   };

   const [formData, setFormData] = useState(initialFormData)

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prevData) => {
         let updatedData = {
            ...prevData,
            [name]: value,
         };
         return updatedData;
      });
   };

   const handleDataChange = (name, newData) => {
      if (newData) {

         setFormData((prevData) => ({
            ...prevData,
            [name]: newData?.[name],
         }));
      } else {
         setFormData((prevData) => ({
            ...prevData,
            [name]: newData,
         }));
      }
   };

   const handleCloseDialog = () => {
      setFormData(initialFormData);
      onClose();
   };

   const handleChangeEndOfRepitition = (event) => {
      setEndOfRepitition(event.target.value === 'true');
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      show();
      let submitData = { ...formData };
      submitData.startDate = format(submitData.startDate, 'yyyy-MM-dd');
      if (endOfRepitition === true) {
         submitData.endDate = format(submitData.endDate, 'yyyy-MM-dd');
      } else {
         submitData.endDate = null;
      }
      submitData.langId = localStorage.getItem("i18nextLngId")

      try {
         const response = await editRosterItem(submitData);
         if (response.data.message === 'Successful' || response.data.message === 'Thành công') {
            handleCloseDialog();
            toast.success(t('common.edit-roster-item-successfully'));
            onRefresh();
         } else {
            toast.error(response.data.message);
         }
      } catch (error) {
         if (error.response) {
            if (error.response.status !== 401) {
               toast.error(t(error.response.data));
            } 
         } 
         console.error('There was an error creating the leave!', error);
      } finally {
         hide();
      }
   };


   return (
      <CustomDialog maxWidth={'md'} open onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>
                     {t('common.roster-items')} - {selectedRosterItem?.weekDate ? format(new Date(selectedRosterItem.weekDate), genaralSettingDateFormat) : format(new Date(), genaralSettingDateFormat)}
                  </p>
               </div>
               <GrayIconButton onClick={handleCloseDialog}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>
         <DialogContent>
            <div
              className='grid grid-cols-2 gap-x-10 gap-y-3 relative mt-5'
              data-tour="shift-and-roster-edit-item-input"
            >
               <div className='col-span-2'>
                  <label className='block text-sm'>
                     {t('common.shift')}
                     <span className='text-[#EF4444] ml-1'>*</span>
                  </label>
                  <div className='mt-2'>
                     <CustomSelect
                        className="min-w-60"
                        name="shift"
                        value={formData.shift}
                        onChange={handleInputChange}
                        options={filteredShifts}
                        dataType="shift"
                     />
                  </div>
               </div>
               <div className='col-span-2'>
                  <div className=''>
                     <label className='block text-sm'>
                        {t('common.start-date')}
                        <span className='text-[#EF4444] ml-1'>*</span>
                     </label>
                     <div className='mt-2'>
                        <CustomDateTimePicker
                           mode='single'
                           value={formData.startDate}
                           minDate={rosterStartDate}
                           maxDate={rosterEndDate ?? undefined}
                           onChange={(newValue) =>
                              handleInputChange({
                                 target: {
                                    name: 'startDate',
                                    value: newValue,
                                 },
                              })
                           }
                           disableTime
                           showOnlyDate
                        />
                     </div>
                  </div>
                  <div className='pt-0'>
                     <label className='block text-sm mt-2'>
                        {t('common.end-of-repetition')}
                     </label>
                     <FormControl component='fieldset' className='w-full mt-2'>
                        <RadioGroup
                           aria-label='End of repitition'
                           name='endOfRepitition'
                           value={endOfRepitition}
                           onChange={handleChangeEndOfRepitition}
                           className='!grid grid-cols-1 md:grid-cols-2 gap-4 w-full !m-0'
                        >
                           <FormControlLabel
                              className='!m-0 items-center'
                              value={false}
                              control={<Radio size='small' />}
                              label={t('common.no-end-date')}
                           />
                           <div className='flex gap-2'>
                              <FormControlLabel
                                 className='!m-0 items-center'
                                 value={true}
                                 control={<Radio size='small' />}
                                 label={t('common.end-by')}
                              />
                              <div className='w-[270px] max-w-full'>
                                 <CustomDateTimePicker
                                    mode='single'
                                    value={formData.endDate}
                                    minDate={rosterStartDate}
                                    maxDate={rosterEndDate ?? undefined}
                                    disabled={!endOfRepitition}
                                    onChange={(newValue) =>
                                       handleInputChange({
                                          target: {
                                             name: 'endDate',
                                             value: newValue,
                                          },
                                       })
                                    }
                                    disableTime
                                    showOnlyDate
                                 />
                              </div>
                           </div>
                        </RadioGroup>
                     </FormControl>
                  </div>
                  {/* <p className='pt-3 text-[#DC2626]'>* ({t('common.required-fields')})</p> */}
               </div>

            </div>
         </DialogContent>
         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={handleCloseDialog}>
                  <p>{t('button.cancel')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton onClick={handleSubmit} >
                  <p>{t('button.update')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default EditRosterItemsDialog;
